import { z } from 'zod';
import { ChatActionType } from '../enums/index.js';
import { type Quant } from './chat.chart.js';
import type { Chat } from './chat.js';

// Shared form data structure for log interaction actions
export const FormDataItemModel = z.object({
  fieldLabel: z.string(),
  value: z.any(),
});
export type FormDataItem = z.infer<typeof FormDataItemModel>;

export const FormDataArrayModel = z.array(FormDataItemModel).optional();
export type FormDataArray = z.infer<typeof FormDataArrayModel>;

export const ReaskWithHcpModel = z.object({
  type: z.literal(ChatActionType.ReaskWithHcp),
  payload: z.object({
    hcpId: z.number(),
    queryId: z.number(),
  }),
});
export type ReaskWithHcpChatAction = z.infer<typeof ReaskWithHcpModel>;

export const SelectHcpSubjectModel = z.object({
  type: z.literal(ChatActionType.SelectHcpSubject),
  payload: z.object({
    hcpId: z.union([z.number(), z.string()]),
    hcpName: z.string(),
  }),
});
export type SelectHcpSubjectChatAction = z.infer<typeof SelectHcpSubjectModel>;

export const LocationItemModel = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('browser'),
    latitude: z.number(),
    longitude: z.number(),
  }),
  z.object({
    type: z.literal('search'),
    longitude: z.number(),
    latitude: z.number(),
    name: z.string(),
    id: z.string(),
  }),
  z.object({
    type: z.literal('entity'),
    longitude: z.number(),
    latitude: z.number(),
    name: z.string(),
    id: z.string(),
  }),
]);
export type SelectLocationItem = z.infer<typeof LocationItemModel>;

export type DeprecatedSelectLocationChatAction = {
  type: ChatActionType.SelectLocation;
  payload: never;
};

export type DeprecatedBuildRouteChatAction = {
  type: ChatActionType.BuildRoute;
  payload: never;
};

export const SubmitFormWorkflowModel = z.object({
  type: z.literal(ChatActionType.SubmitFormWorkflow),
  payload: z.object({
    queryId: z.number(),
    workflowType: z.string(), // the workflow identifier, under the name drafts and messages have always stored it in
    workflowIdentifier: z.uuid().optional(), // the same identifier; wins when both are sent
    formIdentifier: z.string().optional(), // the form by identifier; must be one of the chat's brand, and agree with formParentId if both are sent
    formParentId: z.number().optional(),
    formVersion: z.string().optional(),
    formState: z
      .enum(['initial', 'manual-entry', 'speech-entry', 'draft', 'submitted', 'cancelled', 'probing'])
      .optional(),
    formData: FormDataArrayModel,
    aiState: z.any().optional(),
    draftId: z.string().optional(),
    // The revision of the draft the card was built from. A card older than the draft is refused; one that names none is accepted.
    revision: z.number().int().min(0).optional(),
    submittedAt: z.string().optional(),
    message: z.string().optional(),
  }),
});
export type SubmitFormWorkflowChatAction = z.infer<typeof SubmitFormWorkflowModel>;

export const ChartQuantAnalysesChatActionModel = z.object({
  type: z.literal(ChatActionType.ChartQuantAnalyses),
  quantId: z.number(),
  quantItemId: z.number(),
  query: z.string(),
  projectId: z.number(),
});
export type ChartQuantAnalysesChatAction = z.infer<typeof ChartQuantAnalysesChatActionModel>;

export const QueryFilesChatActionModel = z.object({
  type: z.literal(ChatActionType.QueryFiles),
  query: z.string(),
});
export type QueryFilesChatAction = z.infer<typeof QueryFilesChatActionModel>;

export const FindQuotesChatActionModel = z.object({
  type: z.literal(ChatActionType.FindQuotes),
  subject: z.string(),
});
export type FindQuotesChatAction = z.infer<typeof FindQuotesChatActionModel>;

// Zod schema for Chat.Attachment.AskSurveyQuestionSuggestedAction
export const QuerySurveyQuestionChatActionModel = z.object({
  type: z.literal(ChatActionType.QuerySurveyQuestion),
  project: z.object({
    id: z.number(),
    name: z.string(),
  }),
  question: z.object({
    id: z.number(),
    base: z.object({ id: z.number() }),
    ordinal: z.number(),
    value: z.string(),
  }),
  survey: z.object({
    id: z.number(),
    name: z.string(),
  }),
});
export type QuerySurveyQuestionChatAction = z.infer<typeof QuerySurveyQuestionChatActionModel>;

export type SmartRoutingChatAction = {
  type: ChatActionType.SmartRouting;
  payload: {
    smartRoutingState: Chat.QueryState.SmartRouting;
  };
};

export const SummarizeFolderChatActionModel = z.object({
  type: z.literal(ChatActionType.SummarizeFolder),
  payload: z.object({
    folderId: z.number(),
  }),
});
export type SummarizeFolderChatAction = z.infer<typeof SummarizeFolderChatActionModel>;

export const LoadDraftModel = z.object({
  type: z.literal(ChatActionType.LoadDraft),
  payload: z.object({
    queryId: z.number(),
    draftId: z.string(),
  }),
});
export type LoadDraftChatAction = z.infer<typeof LoadDraftModel>;

export const InitiateFormWorkflowModel = z.object({
  type: z.literal(ChatActionType.InitiateFormWorkflow),
  payload: z.object({
    workflowIdentifier: z.uuid(),
  }),
});
export type InitiateFormWorkflowChatAction = z.infer<typeof InitiateFormWorkflowModel>;

export type AnswerQuantAnalysesAction = {
  quantId: number;
  quantItemId: number;
  query: string;
  projectId: number;
  type: ChatActionType.AnswerQuantAnalyses;
};

export type ChartQuantAnalysesAction = {
  quantId: number;
  quantItemId: number;
  query: string;
  projectId: number;
  type: ChatActionType.ChartQuantAnalyses;
};

export type FindQuantAnalysesAction = {
  quants: Quant[];
  query: string;
  projectId: number;
  type: ChatActionType.FindQuantAnalyses;
};

export type StartSegmentAnalysisAction = {
  type: ChatActionType.StartSegmentAnalysis;
};

export const StartFrmAccountSearchModel = z.object({
  type: z.literal(ChatActionType.StartFrmAccountSearch),
});
export type StartFrmAccountSearchAction = z.infer<typeof StartFrmAccountSearchModel>;

export const StartHcpSearchModel = z.object({
  type: z.literal(ChatActionType.StartHcpSearch),
});
export type StartHcpSearchAction = z.infer<typeof StartHcpSearchModel>;

export const SearchHcpsLocationModel = z.object({
  id: z.string().nullish(),
  latitude: z.number(),
  longitude: z.number(),
  name: z.string().nullish(),
});

export const SearchHcpsModel = z.object({
  type: z.literal(ChatActionType.SearchHcps),
  payload: z
    .object({
      name: z.string().optional(),
      location: SearchHcpsLocationModel.optional(),
      territoryId: z.string().optional(),
    })
    .refine((payload) => !!payload.location || !!payload.name?.trim(), {
      message: 'At least one search criterion is required.',
    }),
});
export type SearchHcpsAction = z.infer<typeof SearchHcpsModel>;

export const ViewRecommendedHcpsModel = z.object({
  type: z.literal(ChatActionType.ViewRecommendedHcps),
});
export type ViewRecommendedHcpsAction = z.infer<typeof ViewRecommendedHcpsModel>;

export type StartSegmentationBuilderAction = {
  type: ChatActionType.StartSegmentationBuilder;
};

export type ChatWithSegmentAction = {
  type: ChatActionType.ChatWithSegment;
  fromSidebar?: boolean;
};

export const SelectRepSubjectModel = z.object({
  type: z.literal(ChatActionType.SelectRepSubject),
  payload: z.object({
    repId: z.string(),
    repName: z.string(),
  }),
});
export type SelectRepSubjectChatAction = z.infer<typeof SelectRepSubjectModel>;

export const SearchRepsModel = z.object({
  type: z.literal(ChatActionType.SearchReps),
  payload: z.object({
    name: z.string().min(1),
    territoryId: z.string().optional(),
  }),
});
export type SearchRepsChatAction = z.infer<typeof SearchRepsModel>;

export const SelectAccountSubjectModel = z.object({
  type: z.literal(ChatActionType.SelectAccountSubject),
  payload: z.object({
    accountId: z.string(),
    accountName: z.string(),
  }),
});
export type SelectAccountSubjectChatAction = z.infer<typeof SelectAccountSubjectModel>;

export const SearchAccountsModel = z.object({
  type: z.literal(ChatActionType.SearchAccounts),
  payload: z.object({
    name: z.string().min(1),
    territoryId: z.string().optional(),
  }),
});
export type SearchAccountsChatAction = z.infer<typeof SearchAccountsModel>;

export const ExtractFormFromFileModel = z.object({
  type: z.literal(ChatActionType.ExtractFormFromFile),
  payload: z.object({
    queryId: z.number(),
    workflowType: z.string(),
    workflowIdentifier: z.uuid().optional(),
    fileUploadIdentifier: z.string(),
  }),
});
export type ExtractFormFromFileChatAction = z.infer<typeof ExtractFormFromFileModel>;

export type SelectSegmentForChatAction = {
  type: ChatActionType.SelectSegmentForChat;
  segmentIdentifier: string;
  segmentId: number;
  iconUrl?: string | null;
};

// Replies to the analytic planner's checkpoints. The checkpoint ID points
// at paused state held on the server; the versions must match what was
// shown, so a stale card can't resume a changed turn.
const plannerCheckpointIdModel = z.uuid();
const plannerVersionModel = z.string().regex(/^[0-9a-f]{64}$/);

// The option the planner adds to every clarifying question: none of the
// offered choices fit. Choosing it ends the planner's turn, so the card can
// be sent without answering its other questions.
export const PLANNER_NONE_OF_THESE_OPTION_ID = 'none-of-these';

export const PlannerClarifyReplyModel = z.object({
  type: z.literal(ChatActionType.PlannerClarifyReply),
  payload: z.object({
    checkpointId: plannerCheckpointIdModel,
    checkpointVersion: plannerVersionModel,
    answers: z
      .array(z.object({ questionId: z.string().min(1).max(128), optionId: z.string().min(1).max(128) }))
      .min(1)
      .max(32),
  }),
});
export type PlannerClarifyReplyAction = z.infer<typeof PlannerClarifyReplyModel>;

export const PlannerReviewReplyModel = z.object({
  type: z.literal(ChatActionType.PlannerReviewReply),
  payload: z.object({
    checkpointId: plannerCheckpointIdModel,
    planVersion: plannerVersionModel,
    action: z.enum(['confirm', 'cancel']),
    // Remember this turn's answers for later questions in the chat. Optional
    // so a card rendered before this field existed can still be answered.
    remember: z.enum(['none', 'answers']).optional(),
  }),
});
export type PlannerReviewReplyAction = z.infer<typeof PlannerReviewReplyModel>;

export type ChatAction<T extends ChatActionType = ChatActionType> = {
  [ChatActionType.ReaskWithHcp]: ReaskWithHcpChatAction;
  [ChatActionType.SelectHcpSubject]: SelectHcpSubjectChatAction;
  [ChatActionType.StartHcpSearch]: StartHcpSearchAction;
  [ChatActionType.SearchHcps]: SearchHcpsAction;
  [ChatActionType.ViewRecommendedHcps]: ViewRecommendedHcpsAction;
  [ChatActionType.StartFrmAccountSearch]: StartFrmAccountSearchAction;
  [ChatActionType.SelectLocation]: DeprecatedSelectLocationChatAction;
  [ChatActionType.BuildRoute]: DeprecatedBuildRouteChatAction;
  [ChatActionType.SmartRouting]: SmartRoutingChatAction;
  [ChatActionType.ChartQuantAnalyses]: ChartQuantAnalysesChatAction;
  [ChatActionType.QueryFiles]: QueryFilesChatAction;
  [ChatActionType.FindQuotes]: FindQuotesChatAction;
  [ChatActionType.SubmitFormWorkflow]: SubmitFormWorkflowChatAction;
  [ChatActionType.QuerySurveyQuestion]: QuerySurveyQuestionChatAction;
  [ChatActionType.SummarizeFolder]: SummarizeFolderChatAction;
  [ChatActionType.LoadDraft]: LoadDraftChatAction;
  [ChatActionType.AnswerQuantAnalyses]: AnswerQuantAnalysesAction;
  [ChatActionType.FindQuantAnalyses]: FindQuantAnalysesAction;
  [ChatActionType.StartSegmentAnalysis]: StartSegmentAnalysisAction;
  [ChatActionType.ChatWithSegment]: ChatWithSegmentAction;
  [ChatActionType.SelectSegmentForChat]: SelectSegmentForChatAction;
  [ChatActionType.StartSegmentationBuilder]: StartSegmentationBuilderAction;
  [ChatActionType.InitiateFormWorkflow]: InitiateFormWorkflowChatAction;
  [ChatActionType.SelectRepSubject]: SelectRepSubjectChatAction;
  [ChatActionType.SearchReps]: SearchRepsChatAction;
  [ChatActionType.SelectAccountSubject]: SelectAccountSubjectChatAction;
  [ChatActionType.SearchAccounts]: SearchAccountsChatAction;
  [ChatActionType.ExtractFormFromFile]: ExtractFormFromFileChatAction;
  [ChatActionType.PlannerClarifyReply]: PlannerClarifyReplyAction;
  [ChatActionType.PlannerReviewReply]: PlannerReviewReplyAction;
}[T];
